import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Box, Stack, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  CopyToClipboardContent,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@verantos-dev/shared-components';

import { getEventColor } from '../constants/event-colors';
import { AgUiEvent } from '../types/gateway-chat-types';
import { extractRunId, formatEventTimestamp } from '../utils/event-helpers';

interface GatewayProtocolMonitorProps {
  events: AgUiEvent[];
}

const renderEventDetails = (event: AgUiEvent) => {
  const payload = event.payload ?? {};
  const runIdentifier = extractRunId(event);

  switch (event.type) {
    case 'PROGRESS_UPDATE':
      return (
        <Stack spacing={0.5}>
          <Typography variant="body2">
            {typeof payload.message === 'string' ? payload.message : 'Processing...'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {typeof payload.percentage === 'number' ? payload.percentage : 0}% complete
          </Typography>
        </Stack>
      );
    case 'RUN_STARTED':
      return (
        <Typography variant="body2" color="text.secondary">
          {runIdentifier ? `Run ${runIdentifier.substring(0, 8)} started` : 'Run started'}
        </Typography>
      );
    case 'RUN_FINISHED':
      return (
        <Typography variant="body2" color="text.secondary">
          {runIdentifier ? `Run ${runIdentifier.substring(0, 8)} complete` : 'Run complete'}
        </Typography>
      );
    case 'ERROR':
      return (
        <Typography variant="body2" color="error.main">
          {typeof payload.message === 'string' ? payload.message : 'Unknown error'}
        </Typography>
      );
    default:
      return (
        <Box
          component="pre"
          sx={{
            maxHeight: 160,
            overflow: 'auto',
            mt: 1,
            mb: 0,
            p: 1,
            borderRadius: 1,
            backgroundColor: 'grey.50',
            border: '1px solid',
            borderColor: 'grey.200',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontSize: '0.75rem',
          }}
        >
          {JSON.stringify(payload, null, 2)}
        </Box>
      );
  }
};

const GatewayEventCard = ({
  event,
  onClick,
}: {
  event: AgUiEvent;
  onClick: (event: AgUiEvent) => void;
}) => {
  const accent = getEventColor(event.type);

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        borderLeft: `4px solid ${accent}`,
        backgroundColor: 'background.paper',
        boxShadow: '0px 1px 3px rgba(15, 23, 42, 0.08)',
        cursor: 'pointer',
        transition: 'transform 0.1s ease, box-shadow 0.1s ease',
        '&:hover': {
          transform: 'translateX(2px)',
          boxShadow: '0px 4px 12px rgba(15, 23, 42, 0.12)',
        },
      }}
      onClick={() => onClick(event)}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="caption" sx={{ fontWeight: 700, color: accent }}>
          {event.type}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatEventTimestamp(event.timestamp)}
        </Typography>
      </Stack>

      <Box mt={1}>{renderEventDetails(event)}</Box>
    </Box>
  );
};

export const GatewayProtocolMonitor = ({ events }: GatewayProtocolMonitorProps) => {
  const [selectedEvent, setSelectedEvent] = useState<AgUiEvent | null>(null);
  const intl = useIntl();

  const highlightedJson = useMemo(() => {
    if (!selectedEvent) return '';
    return JSON.stringify(selectedEvent.payload ?? {}, null, 2);
  }, [selectedEvent]);

  return (
    <>
      <Box
        sx={{
          borderLeft: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          backgroundColor: 'grey.50',
        }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <CloudUploadIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Protocol Monitor
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Live AG-UI events
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          {events.length === 0 ? (
            <Stack
              flex={1}
              alignItems="center"
              justifyContent="center"
              spacing={1}
              color="text.secondary"
            >
              <Typography variant="h4" component="div">
                📡
              </Typography>
              <Typography variant="body2">Waiting for events…</Typography>
            </Stack>
          ) : (
            events.map((event) => {
              const runIdentifier = extractRunId(event);
              const eventKey = runIdentifier
                ? `${runIdentifier}-${event.timestamp}`
                : `${event.type}-${event.timestamp}`;

              return <GatewayEventCard key={eventKey} event={event} onClick={setSelectedEvent} />;
            })
          )}
        </Box>
      </Box>

      <Dialog
        open={Boolean(selectedEvent)}
        onClose={() => setSelectedEvent(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle
          showClose
          onClose={() => setSelectedEvent(null)}
          id={selectedEvent?.type ?? 'Event'}
        >
          {selectedEvent?.type ?? 'Event'}
        </DialogTitle>
        <DialogContent dividers>
          {selectedEvent ? (
            <CopyToClipboardContent
              id="gateway-protocol-copy-payload"
              copyText={highlightedJson || 'No payload'}
              copyButtonLabel={intl.formatMessage({
                id: 'app.gatewayProtocolMonitor.copyPayload',
                defaultMessage: 'Copy payload',
              })}
              copiedButtonLabel={intl.formatMessage({
                id: 'app.gatewayProtocolMonitor.copiedPayload',
                defaultMessage: 'Copied',
              })}
              copiedTooltipTitle={intl.formatMessage({
                id: 'app.gatewayProtocolMonitor.copiedPayloadTooltip',
                defaultMessage: 'Copied payload to clipboard',
              })}
              buttonWidth={140}
            >
              <Box
                component="pre"
                sx={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontSize: '0.85rem',
                  backgroundColor: 'grey.100',
                  borderRadius: 2,
                  p: 2,
                  pt: 5,
                  maxHeight: 400,
                  overflow: 'auto',
                }}
              >
                {highlightedJson || 'No payload'}
              </Box>
            </CopyToClipboardContent>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
};
